import {
	type ComponentPropsWithoutRef,
	type FC,
	useEffect,
	useRef
} from 'react';
import { drawAudioWaveFrame, getNextAudioWaveLevel } from './utils';

export type AudioWaveProps = ComponentPropsWithoutRef<'div'> & {
	level: number;
};

export const AudioWave: FC<AudioWaveProps> = ({
	level,
	className = 'pointer-events-none relative h-full w-full',
	...rest
}) => {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const latestLevelRef = useRef(level);
	const sizeRef = useRef({ height: 0, width: 0 });
	latestLevelRef.current = level;

	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const updateSize = () => {
			const { height, width } = canvas.getBoundingClientRect();
			sizeRef.current = { height, width };
		};
		let smoothedLevel = latestLevelRef.current;
		let frameId = 0;
		const resizeObserver = new ResizeObserver(updateSize);

		updateSize();
		resizeObserver.observe(canvas);

		const tick = (now: number) => {
			smoothedLevel = getNextAudioWaveLevel(
				smoothedLevel,
				latestLevelRef.current
			);
			drawAudioWaveFrame({
				canvas,
				cssHeight: sizeRef.current.height,
				cssWidth: sizeRef.current.width,
				frameTime: now,
				smoothedLevel
			});
			frameId = window.requestAnimationFrame(tick);
		};

		frameId = window.requestAnimationFrame(tick);

		return () => {
			resizeObserver.disconnect();
			window.cancelAnimationFrame(frameId);
		};
	}, []);

	return (
		<div
			className={className}
			{...rest}
		>
			<canvas
				className="h-full w-full"
				data-testid="audio-wave-canvas"
				ref={canvasRef}
			/>
		</div>
	);
};
